// One searchable list of things a user can avoid: ingredient families first,
// then individual ingredients.
//
// Shared by the two avoid-list pickers — Profile → "Allergies & Foods to Avoid"
// and step 4 of onboarding. They are two separate screens that must offer the
// same thing, and they drifted once already; the matching rules live here so
// there is one place to change them.
//
// A GROUP IS ONE ENTRY, ALWAYS. Picking "fish" stores the word "fish" and
// renders one chip. The species it covers are expanded on the server on every
// query, so a fish added to the catalog next month is already excluded — there
// is nothing here to enumerate, and expanding a group into six chips would
// imply six decisions the user could undo separately.

// Uniform shape for both kinds, so a picker never branches on which it holds.
// `key` is what React keys and de-dupe compare on: ids and slugs live in
// different namespaces and an ingredient with id 3 must not collide with a
// group.
export function groupOption(group) {
  return {
    key: `group:${group.slug}`,
    kind: "group",
    id: null,
    slug: group.slug,
    name: group.label,
    memberCount: group.member_count ?? 0,
    aliases: group.aliases || [],
  };
}

export function ingredientOption(ingredient) {
  return {
    key: `ingredient:${ingredient.id}`,
    kind: "ingredient",
    id: ingredient.id,
    slug: null,
    name: ingredient.name,
    memberCount: 0,
    aliases: [],
  };
}

// Normalises whatever the blacklist endpoint returned into the same shape. The
// server already tags each row with `kind`, so this is a rename rather than a
// guess.
export function selectionOption(row) {
  return row.kind === "group"
    ? groupOption({
        slug: row.slug,
        label: row.name,
        member_count: row.member_count,
        aliases: row.aliases,
      })
    : ingredientOption(row);
}

// Groups first. They are the entries that answer "I don't eat fish" in one tap,
// which is the whole reason the taxonomy exists — burying them under an
// alphabetical ingredient list would leave the reported problem in place.
// Groups the vocabulary marks `avoidable: false` (spices, cooking oil) are
// dropped: the blacklist is a hard filter, and avoiding either would empty the
// user's own catalog.
//
// `coveredGroupSlugs` are families the user's diet setting already excludes.
// They are dropped rather than shown-and-ignored: offering "poultry" to someone
// who has just told us they are vegetarian invites them to make a choice that
// changes nothing, and two controls that appear to disagree about who is in
// charge of meat is exactly the "duplicate or fight" this composition avoids.
// The diet does the excluding; this list stays for everything a diet has no
// opinion about (nuts, mushrooms, tomato). Callers doing INCLUSION rather than
// exclusion — the builder's feature-ingredient picker — pass nothing and keep
// the full list, because featuring an ingredient is a bias and a vegetarian may
// still legitimately search the word "fish".
export function buildAvoidOptions(groups, ingredients, coveredGroupSlugs = []) {
  const covered = new Set(coveredGroupSlugs || []);
  const groupOpts = (groups || [])
    .filter((g) => g.avoidable && !covered.has(g.slug))
    .map(groupOption);
  const ingredientOpts = (ingredients || []).map(ingredientOption);
  return [...groupOpts, ...ingredientOpts];
}

// Matches name and, for groups, the aliases the server sent — so "seafood"
// offers fish and shellfish even though no row and no label is called that,
// and the picker agrees with what a recipe search would have matched.
export function matchesAvoidQuery(option, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return true;
  if (option.name.toLowerCase().includes(q)) return true;
  return (option.aliases || []).some((a) => a.toLowerCase().includes(q));
}

export function isPicked(option, picked) {
  return (picked || []).some((p) => p.key === option.key);
}

// Suggestions for a query: unpicked, matching, groups still first.
export function suggestAvoidOptions(options, picked, query, limit = 6) {
  return (options || [])
    .filter((o) => !isPicked(o, picked))
    .filter((o) => matchesAvoidQuery(o, query))
    .slice(0, limit);
}
